/**
 * Center place to adjust fields order in several places
 *
 * @date 2025/12/11
 */
import * as _ from 'lodash-es';

import {
  moveElementToBack,
  swapFields,
  pipeFuncs,
  moveElementToFront,
} from '@/utils';

import {
  FIELD_D1,
  FIELD_D2,
  FIELD_D3,
  FIELD_D4,
  FIELD_H1,
  FIELD_H2,
  FIELD_H3,
  FIELD_L0,
  FIELD_W1,
  FIELD_W2,
  FIELD_W3,
  FIELD_Z0,
  LENGTH_STR_FIELD,
  DUCTSHAPES as SP,
  OMIT_FROM_ROUND,
  OMIT_FROM_SQUARE,
  OMIT_FROM_EDIT,
  VERTICAL_LENGTH_FIELD,
  EQUIPMENT_FIELDS_IN_ORDER,
  SPECIAL_JOB_ITEM,
  isFlexShape,
  isRoundShape,
  isRoundRectShape,
} from '../types';

/** shared context threaded through the reorder pipe stages */
type ReorderCtx = { entityType: string; shape?: string };

/**
 * serious of reorder function for side panel fields
 *
 * @date 2025/12/11
 * @param dimensionForType fields to re-order with couple of pipe function
 * @param entityType symbol entity to check in pipe
 * @param shape current display shape - ROUND | RECT | ROUNDRECT | FLEX,
 *   used to pick the right field order @2026/08/27
 *
 * @returns
 */
export const pipelineProcessing = (
  dimensionForType: string[],
  entityType: string,
  shape?: string,
) => {
  const pipes = pipeFuncs<string, ReorderCtx>(
    reOrderEquipmentFields, // short-circuits for equipment, skip remaining stages
    reOrderFieldsForLength,
    reOrderFieldsForDiameters,
    pushZoneTOBottom,
  );
  return pipes(dimensionForType, { entityType, shape });
};

/**
 * part of pipes function to handl equipment symbol display in side panel
 * @date 2025/10/22
 * @param fields
 * @returns
 */
const pushZoneTOBottom = (fields: string[]) => {
  const fieldIndex = fields.indexOf(FIELD_Z0);
  if (fieldIndex > -1) {
    const zoneToBottom = moveElementToBack(fields, fieldIndex);
    return zoneToBottom;
  }
  return fields;
};

/**
 * ! Only checking `ctx` to return fixed equipment order
 * @log 2025/12/12
 *
 * check if its equipment by fields checking,
 * if yes return hard coded list, or the previous list
 *
 * @log fix the `zone` field above other files - 2025/12/11
 * @log add `multiplier` 2025/10/20
 *
 * @param dimensionForType fields for equipment symbol
 * @param ctx could be entityType
 * @returns
 */
export const reOrderEquipmentFields = (
  dimensionForType: string[],
  ctx?: ReorderCtx,
) => {
  const isEquipment = ctx?.entityType == SPECIAL_JOB_ITEM.EQMT;
  if (isEquipment) {
    return EQUIPMENT_FIELDS_IN_ORDER;
  }
  return dimensionForType;
};

/**
 * - put `length_unit_str` to the back of `width_1`
 * @date 2024/08/27
 * - put `width_1` ahead of `height_1`
 * @date 2025/01/28
 * - refactored to use name-based ops to avoid stale index bugs
 * @date 2026/02/19
 * - group ALL width/height pairs together (w1, w2?, height after),
 *   fixing broken order when both w1/h1 AND w2/h2 groups are present
 * @date 2026/08/27
 * - shape-aware ordering: ROUND only cares about diameter fields, RECT
 *   only cares about width/height, ROUNDRECT displays width, height,
 *   then diameter - fixes the FLEX early-return wrongly firing for
 *   ROUNDRECT-capable entities that also carry a `diameter_1` field
 * @date 2026/08/27
 * - replaced the per-shape if/else chain with a declarative
 *   `SHAPE_FIELD_ORDER` table + a single grouping helper, so every shape
 *   (including FLEX) goes through the same trailing `putFieldToBottom`
 *   cleanup instead of FLEX early-returning past it
 * @date 2026/08/31
 */
const reOrderFieldsForLength = (
  dimensionForType: string[],
  ctx?: ReorderCtx,
) => {
  let result = groupFieldsByShapeOrder([...dimensionForType], ctx?.shape);

  // push vertical_length and length to back
  result = putFieldToBottom(result, VERTICAL_LENGTH_FIELD);
  result = putFieldToBottom(result, FIELD_L0);

  return result;
};

/**
 * Reorder width/height pairs so all widths precede all heights:
 * [h1, h2?, h3?, w1, w2?, w3?] --> [w1, w2?, w3?, h1, h2?, h3?]
 */
const swapWidthsBeforeHeights = (fields: string[]) => {
  const pairs: [string, string][] = [
    [FIELD_H3, FIELD_W3],
    [FIELD_H2, FIELD_W2],
    [FIELD_H1, FIELD_W1],
  ];
  return pairs.reduce(
    (acc, [h, w]) =>
      acc.includes(h) && acc.includes(w) ? swapFields(acc, h, w) : acc,
    fields,
  );
};

/**
 * Move the given fields into one contiguous block (in the given order),
 * placed at the position of the earliest of them. Other fields keep their
 * relative order around the block.
 * @date 2026/08/27
 */
const moveFieldsIntoBlock = (fields: string[], block: string[]) => {
  const blockSet = new Set(block);
  const firstIndex = fields.findIndex((f) => blockSet.has(f));
  if (firstIndex === -1) return fields;
  const rest = fields.filter((f) => !blockSet.has(f));
  rest.splice(firstIndex, 0, ...block);
  return rest;
};

/**
 * Declarative width/height/diameter/length display order per shape.
 * Fields absent from the symbol's actual field list are dropped before
 * grouping, so a shorter list (eg. ROUND with no `length_unit_str`) is
 * handled the same as the full one.
 * @date 2026/08/31
 */
const SHAPE_FIELD_ORDER: Record<string, string[]> = {
  FLEX: [FIELD_D1, LENGTH_STR_FIELD],
  ROUND: [FIELD_D1, FIELD_D2, FIELD_D3, FIELD_D4, LENGTH_STR_FIELD],
  ROUNDRECT: [
    FIELD_W1,
    FIELD_W2,
    FIELD_W3,
    FIELD_H1,
    FIELD_H2,
    FIELD_H3,
    FIELD_D1,
    FIELD_D2,
    FIELD_D3,
    FIELD_D4,
  ],
  DEFAULT: [
    FIELD_W1,
    FIELD_W2,
    FIELD_W3,
    FIELD_H1,
    FIELD_H2,
    FIELD_H3,
    FIELD_D1,
    FIELD_D2,
    FIELD_D3,
    FIELD_D4,
    LENGTH_STR_FIELD,
  ],
};

const resolveShapeOrderKey = (shape?: string) => {
  if (isFlexShape(shape)) return 'FLEX';
  if (isRoundShape(shape)) return 'ROUND';
  if (isRoundRectShape(shape)) return 'ROUNDRECT';
  return 'DEFAULT';
};

/**
 * Group width/height/diameter/length fields into one contiguous block,
 * ordered per `SHAPE_FIELD_ORDER` for the resolved shape.
 * @date 2026/08/31
 */
const groupFieldsByShapeOrder = (fields: string[], shape?: string) => {
  const order = SHAPE_FIELD_ORDER[resolveShapeOrderKey(shape)];
  const block = order.filter((f) => fields.includes(f));
  return moveFieldsIntoBlock(fields, block);
};

/**
 * put `diameter_2`, `diameter_3`, `diameter_4` to the end of list
 * @date 2025/05/28, 29
 */
const reOrderFieldsForDiameters = (fields: string[]) => {
  return [FIELD_D2, FIELD_D3, FIELD_D4].reduce(
    (acc, field) => putFieldToBottom(acc, field),
    fields,
  );
};

export const reOrderPipeLengthFields = (plFields: string[]) => {
  let filteredFields = plFields;
  // then, remove "length_1", "length_unit_str" - @2025/04/22
  filteredFields = _.difference(filteredFields, OMIT_FROM_EDIT);

  const zIndex = filteredFields.indexOf(FIELD_Z0);
  let orderedFields = filteredFields;
  if (zIndex > -1) {
    // @2025/04/03
    orderedFields = moveElementToBack(filteredFields, zIndex) as string[];
  }
  return orderedFields;
};

export const reOrderDuctLengthFields = (dlFields: string[], shape: string) => {
  let filteredFields = dlFields;
  // first, remove shape related fields
  if (shape === SP.ROUND) {
    filteredFields = _.difference(dlFields, OMIT_FROM_ROUND);
  }
  if (shape === SP.SQUARE) {
    filteredFields = _.difference(dlFields, OMIT_FROM_SQUARE);
    filteredFields = swapFields(filteredFields, FIELD_H1, FIELD_W1);
  }
  // ! harde-coded field for FLEX
  // @2025/11/25
  if (shape === SP.FLEX) {
    filteredFields = [FIELD_D1, FIELD_Z0];
  }
  // then, remove "length_1", "length_unit_str" - @2025/04/22
  filteredFields = _.difference(filteredFields, OMIT_FROM_EDIT);

  let orderedFields = filteredFields;
  const zIndex = filteredFields.indexOf(FIELD_Z0);
  if (zIndex > -1) {
    // @2025/04/03
    orderedFields = moveElementToBack(filteredFields, zIndex) as string[];
  }
  return orderedFields;
};

/**
 * Filter DUCT fields from selected shape type
 * @param ductFields
 * @param shape
 * @returns
 */
export const filterFieldsByShape = (ductFields: string[], shape: string) => {
  let filteredFields = ductFields;
  if (shape === SP.ROUND) {
    filteredFields = _.difference(ductFields, OMIT_FROM_ROUND);
  }
  if (shape === SP.SQUARE) {
    filteredFields = _.difference(ductFields, OMIT_FROM_SQUARE);
  }
  // ! remove `length_unit_str` - @2025/05/07
  filteredFields = filteredFields.filter((f) => f !== LENGTH_STR_FIELD);

  return filteredFields;
};

// re-order fields for `vertical_length` property
// @2025/02/28, 05/30
export const reOrderDuctField = (fields: string[]) => {
  // ! first order with name to make it normal:
  // @2025/05/30
  let result = [...fields].sort();

  result = putFieldToBottom(result, VERTICAL_LENGTH_FIELD);
  // @2025/03/07
  result = putFieldToBottom(result, FIELD_L0);

  return result;
};

/**
 * Ensure `diameter_2` is placed immediately after `diameter_1`
 * @date 2025/04/03
 */
export const placeD2AfterD1 = (fields: string[]) => {
  const d1Index = fields.indexOf(FIELD_D1);
  const d2Index = fields.indexOf(FIELD_D2);
  if (d1Index > -1 && d2Index > -1 && d2Index !== d1Index + 1) {
    const withoutD2 = fields.filter((f) => f !== FIELD_D2);
    const d1NewIndex = withoutD2.indexOf(FIELD_D1);
    withoutD2.splice(d1NewIndex + 1, 0, FIELD_D2);
    return withoutD2;
  }
  return fields;
};

/**
 * Move target field to the bottom of field list
 * @data 2025/04/03
 * @param fields list of symbol property
 * @param targetField some field need to move bottom
 * @returns re-ordered fields
 */
export const putFieldToBottom = (fields: string[], targetField: string) => {
  const fieldIndex = fields.indexOf(targetField);
  if (fieldIndex > -1) return moveElementToBack(fields, fieldIndex);
  return fields;
};

/**
 * Re-order fields by entity type in a hard-coded way
 * FIXME: https://taksoai.atlassian.net/browse/TAK-699
 * @date 2024/10/10
 * @param fields common fields
 * @returns ordered fileds
 */
export const orderFieldsDisplayInBatch = (
  entityType: string,
  fields: string[],
) => {
  if (entityType === SPECIAL_JOB_ITEM.EQMT) {
    // add missing field `zone`:
    // FIXME: https://taksoai.atlassian.net/browse/TAK-890
    // @2025/07/29
    // ! use a hard-coded equipment list!
    // @2025/10/20
    return reOrderEquipmentFields(fields);
  }
  // ! order by alphabetical order
  // @2025/06/11, 12/24
  const finalFields = swapWidthsBeforeHeights([...fields].sort());
  // move `system` to front
  if (finalFields.includes('system')) {
    return moveElementToFront(finalFields, 'system');
  }
  return finalFields;
};
